import { yupToFormErrors } from 'formik';
import isNil from 'lodash/isNil';
import * as yup from 'yup';

import {
  InstitutionalIndicationOrderType,
  InterestLevelInterestUnit,
  InterestLevelLimitType,
  OfferingType,
} from '../../../../../../../../graphql';
import {
  contactsArraySchema,
  ContactsArraySchemaContext,
  InvestorContact,
} from '../../../investor-contact-information/InvestorContactInformationForm.model';
import { ConvertibleOrderType } from './getConvertibleOrderType';

export type InterestLevelValue = {
  interestQuantity: number | null;
  interestUnit: InterestLevelInterestUnit | null;
  orderType: InstitutionalIndicationOrderType | ConvertibleOrderType | null;
  limitPrice: number | null;
  limitType: InterestLevelLimitType | null;
  coupon: number | null;
  premium: number | null;
  percentageOfPar: number | null;
};

export type IndicationFormValues = {
  trancheId: string | null;
  instrumentId: string | null;
  currencyCode: string | null;
  selectedInterestUnit?: InterestLevelInterestUnit | null;
  interestLevels: InterestLevelValue[];
};

export const emptyInterestLevelValue: InterestLevelValue = {
  interestQuantity: null,
  interestUnit: null,
  orderType: null,
  limitPrice: null,
  limitType: null,
  coupon: null,
  premium: null,
  percentageOfPar: null,
};

const interestQuantityUniqueTest = (value: number | null, context: yup.TestContext): boolean => {
  const interestLevels: InterestLevelValue[] = context.from?.[1].value.interestLevels ?? [];

  if (isNil(value)) {
    return true;
  }

  const levelsWithSameValue = interestLevels.filter(
    ({ interestQuantity }) => interestQuantity === value
  );

  return levelsWithSameValue.length <= 1;
};

const interestQuantityMarketIncreaseTest = (
  marketInterestQuantity: number | null,
  context: yup.TestContext
): boolean => {
  const interestLevels: InterestLevelValue[] = context.from?.[1].value.interestLevels ?? [];

  if (isNil(marketInterestQuantity)) {
    return true;
  }

  return !interestLevels.some(
    ({ orderType, interestQuantity }) =>
      orderType === InstitutionalIndicationOrderType.Limit &&
      !isNil(interestQuantity) &&
      interestQuantity < marketInterestQuantity
  );
};

const interestQuantityLimitIncreaseTest = (
  limitInterestQuantity: number | null,
  context: yup.TestContext
): boolean => {
  const interestLevels: InterestLevelValue[] = context.from?.[1].value.interestLevels ?? [];
  const currentInterestLevel: InterestLevelValue = context.parent;

  if (
    isNil(limitInterestQuantity) ||
    isNil(currentInterestLevel.limitPrice) ||
    isNil(currentInterestLevel.limitType)
  ) {
    return true;
  }

  if (currentInterestLevel.limitType === InterestLevelLimitType.Discount) {
    return !interestLevels.some(
      ({ interestQuantity, limitPrice, limitType }) =>
        !isNil(interestQuantity) &&
        !isNil(limitPrice) &&
        !isNil(limitType) &&
        ((limitInterestQuantity > interestQuantity &&
          currentInterestLevel.limitPrice! < limitPrice) ||
          (limitInterestQuantity < interestQuantity &&
            currentInterestLevel.limitPrice! > limitPrice))
    );
  } else {
    // Price / Premium
    return !interestLevels.some(
      ({ interestQuantity, limitPrice, limitType }) =>
        !isNil(interestQuantity) &&
        !isNil(limitPrice) &&
        !isNil(limitType) &&
        ((limitInterestQuantity > interestQuantity &&
          currentInterestLevel.limitPrice! > limitPrice) ||
          (limitInterestQuantity < interestQuantity &&
            currentInterestLevel.limitPrice! < limitPrice))
    );
  }
};

const interestLevelConfigurationUniqueErrorMessage =
  'You cannot submit two interest levels with the same limit configuration';

function interestLevelConfigurationUniqueTest(
  _: number | null | undefined,
  { options, parent }: yup.TestContext
): boolean {
  const { interestLevels = [] } =
    (options.context as CreateIndicationValidationSchemaContext) ?? {};
  const interestLevelsWithoutParent = interestLevels.filter(level => level !== parent);
  const hasSameConfiguration = interestLevelsWithoutParent.some(
    level =>
      level.percentageOfPar === parent.percentageOfPar &&
      level.coupon === parent.coupon &&
      level.premium === parent.premium
  );
  return !hasSameConfiguration;
}

const isPremiumLowerWithHigherDemandErrorMessage = 'Premium % needs to be lower with higher demand';

function isPremiumLowerWithHigherDemand(
  _: number | null | undefined,
  { options, parent }: yup.TestContext
): boolean {
  const { interestLevels = [] } =
    (options.context as CreateIndicationValidationSchemaContext) ?? {};
  const interestLevelsWithoutParent = interestLevels.filter(level => level !== parent);
  const hasInvalidLowerDemandWithLowerPremium = interestLevelsWithoutParent.some(level => {
    if (parent.premium === null) {
      return (
        level.premium !== null &&
        !isNil(level.interestQuantity) &&
        level.interestQuantity < parent.interestQuantity
      );
    }
    return (
      level.premium !== null &&
      level.premium < parent.premium &&
      level.interestQuantity !== null &&
      level.interestQuantity <= parent.interestQuantity
    );
  });
  return !hasInvalidLowerDemandWithLowerPremium;
}

const isCouponHigherWithHigherDemandErrorMessage = 'Coupon % needs to be higher with higher demand';

function isCouponHigherWithHigherDemandTest(
  _: number | null | undefined,
  { options, parent }: yup.TestContext
): boolean {
  const { interestLevels = [] } =
    (options.context as CreateIndicationValidationSchemaContext) ?? {};
  const interestLevelsWithoutParent = interestLevels.filter(level => level !== parent);
  const hasInvalidLowerDemandWithHigherCoupon = interestLevelsWithoutParent.some(
    level =>
      level.coupon !== null &&
      level.coupon > parent.coupon &&
      level.interestQuantity !== null &&
      level.interestQuantity <= parent.interestQuantity
  );
  return !hasInvalidLowerDemandWithHigherCoupon;
}

export const InterestLevelValidationSchema = yup.object().shape({
  orderType: yup
    .string()
    .label('Order Type')
    .nullable()
    .required()
    .test('limit-order-only', 'IOI Type is invalid', (value, { options }) => {
      if (options.context?.isLimitIndicationsOnlyEnabled) {
        return value === InstitutionalIndicationOrderType.Limit;
      }
      return true;
    }),
  percentageOfPar: yup
    .number()
    .label('% of Par')
    .positive()
    .nullable()
    .when((_, schema, options) => {
      const { offeringType } = (options.context as CreateIndicationValidationSchemaContext) ?? {};

      if (offeringType === OfferingType.Convertible) {
        return schema
          .test(
            'test-percentageOfPar-lowerPrice-with-higher-demand',
            '% of Par needs to be lower with higher demand',
            function (_, { options, parent }) {
              const { interestLevels = [] } =
                (options.context as CreateIndicationValidationSchemaContext) ?? {};
              const interestLevelsWithoutParent = interestLevels.filter(level => level !== parent);
              const hasInvalidLowerDemandWithLowerPrice = interestLevelsWithoutParent.some(
                level =>
                  level.percentageOfPar !== null &&
                  level.percentageOfPar < parent.percentageOfPar &&
                  level.interestQuantity !== null &&
                  level.interestQuantity <= parent.interestQuantity
              );
              return !hasInvalidLowerDemandWithLowerPrice;
            }
          )
          .test(
            'test-interestLevelConfiguration-unique',
            interestLevelConfigurationUniqueErrorMessage,
            interestLevelConfigurationUniqueTest
          )
          .test(
            'test-percentageOfParIsNoLongerAccepted',
            '% of Par needs to be cleared.',
            function (value, { options }) {
              const { isIndicationPriceAsPercentageParEnabled } =
                (options.context as CreateIndicationValidationSchemaContext) ?? {};
              const hasInvalidPercentageOfParValue =
                !isIndicationPriceAsPercentageParEnabled && value !== null;
              return !hasInvalidPercentageOfParValue;
            }
          );
      }
      return schema;
    }),
  coupon: yup
    .number()
    .label('Coupon')
    .min(0)
    .max(1, ({ label }) => `${label} must be less than or equal to 100%`)
    .nullable()
    .when((_, schema, options) => {
      const { offeringType } = (options.context as CreateIndicationValidationSchemaContext) ?? {};

      if (offeringType === OfferingType.Convertible) {
        return schema
          .test(
            'test-coupon-higherCoupon-with-higher-demand',
            isCouponHigherWithHigherDemandErrorMessage,
            isCouponHigherWithHigherDemandTest
          )
          .test(
            'test-interestLevelConfiguration-unique',
            interestLevelConfigurationUniqueErrorMessage,
            interestLevelConfigurationUniqueTest
          );
      }
      return schema;
    }),
  premium: yup
    .number()
    .label('Premium')
    .min(0)
    .max(1, ({ label }) => `${label} must be less than or equal to 100%`)
    .nullable()
    .when((_, schema, options) => {
      const { offeringType } = (options.context as CreateIndicationValidationSchemaContext) ?? {};
      if (offeringType === OfferingType.Convertible) {
        return schema
          .test(
            'test-premium-lowerPremium-with-higher-demand',
            isPremiumLowerWithHigherDemandErrorMessage,
            isPremiumLowerWithHigherDemand
          )
          .test(
            'test-interestLevelConfiguration-unique',
            interestLevelConfigurationUniqueErrorMessage,
            interestLevelConfigurationUniqueTest
          );
      }
      return schema;
    }),
  interestUnit: yup
    .string()
    .label('Demand Unit')
    .nullable()
    .required()
    .test('share-unit-only', 'Demand Unit is invalid', (value, { options }) => {
      if (options.context?.isOnlySharesUnitOnIndicationsEnabled) {
        return value === InterestLevelInterestUnit.Shares;
      }

      return true;
    }),
  interestQuantity: yup
    .number()
    .label('Demand Quantity')
    .positive()
    .nullable()
    .required()
    .when('interestUnit', {
      is: InterestLevelInterestUnit.Percent,
      then: schema => schema.max(1, 'A Percentage-based Interest Quantity cannot exceed 100%'),
    })
    .test(
      'test-interestQuantity-unique',
      'Demand quantity should not be the same',
      interestQuantityUniqueTest
    )
    .when('orderType', {
      is: InstitutionalIndicationOrderType.Market,
      then: schema =>
        schema.test(
          'test-interestQuantity-marketIncrease',
          'Demand quantity should not increase at higher price',
          interestQuantityMarketIncreaseTest
        ),
    })
    .when('orderType', {
      is: InstitutionalIndicationOrderType.Limit,
      // here are the validations
      then: schema =>
        schema.test(
          'test-interestQuantity-limitIncrease',
          'Demand quantity should not increase at higher price',
          interestQuantityLimitIncreaseTest
        ),
    })
    .when((_, schema, options) => {
      const { offeringType } = (options.context as CreateIndicationValidationSchemaContext) ?? {};
      if (offeringType === OfferingType.Convertible) {
        return schema
          .test(
            'test-interestQuantity-convertibleMarketIncrease',
            'Demand quantity should not increase at higher price',
            (value, { options, parent }) => {
              if (parent.orderType !== ConvertibleOrderType.Market) {
                return true;
              }
              const { interestLevels = [] } =
                (options.context as CreateIndicationValidationSchemaContext) ?? {};
              const interestLevelsWithoutParent = interestLevels.filter(level => level !== parent);

              const hasInvalidLowerDemand = interestLevelsWithoutParent.some(
                interestLevel =>
                  !isNil(interestLevel.interestQuantity) && interestLevel.interestQuantity < value
              );

              return !hasInvalidLowerDemand;
            }
          )
          .test(
            'test-interestQuantity-higherCoupon-with-higher-demand',
            isCouponHigherWithHigherDemandErrorMessage,
            isCouponHigherWithHigherDemandTest
          )
          .test(
            'test-interestQuantity-lowerPremium-with-higher-demand',
            isPremiumLowerWithHigherDemandErrorMessage,
            isPremiumLowerWithHigherDemand
          );
      }
      return schema;
    }),
  limitType: yup
    .string()
    .label('Limit Type')
    .nullable()
    .when('orderType', {
      is: InstitutionalIndicationOrderType.Limit,
      then: schema => schema.required(),
    }),
  limitPrice: yup
    .number()
    .label('Limit Price')
    .nullable()
    .when('orderType', {
      is: InstitutionalIndicationOrderType.Limit,
      then: schema =>
        schema
          .required()
          .positive()
          .test(
            'test-checkUniqueLimitPrice',
            'You cannot submit two interest levels with the same limit configuration',
            function (this, limitPrice) {
              const interestLevels = this.from?.[1].value.interestLevels ?? [];
              return (
                interestLevels.filter(
                  interestLevel =>
                    interestLevel.orderType === InstitutionalIndicationOrderType.Limit &&
                    interestLevel.limitPrice === limitPrice &&
                    interestLevel.limitType === this.parent.limitType
                ).length < 2
              );
            }
          )
          .when('limitType', {
            is: InterestLevelLimitType.Discount,
            then: schema => schema.positive().max(1, 'A Discount Limit cannot exceed 100%'),
          })
          .when('limitType', {
            is: InterestLevelLimitType.Premium,
            then: schema => schema.positive(),
          }),
    }),
});

export const validationSchema = yup.object().shape({
  trancheId: yup.string().label('Tranche').nullable().required(),
  instrumentId: yup.string().label('Demand Instrument').nullable().required(),
  currencyCode: yup.string().label('Demand Currency').nullable().required(),
  interestLevels: yup
    .array()
    .label('Interest Levels')
    .min(1)
    .of(InterestLevelValidationSchema)
    .required(),
});

export type IndicationCreateFormValues = IndicationFormValues & {
  sellSideContacts: InvestorContact[];
};

export type CommonIndicationValidationSchemaContext = {
  interestLevels: InterestLevelValue[];
  isLimitIndicationsOnlyEnabled: boolean;
  isIndicationPriceAsPercentageParEnabled: boolean;
  isOnlySharesUnitOnIndicationsEnabled: boolean;
  offeringType: OfferingType;
};

export type CreateIndicationValidationSchemaContext = CommonIndicationValidationSchemaContext &
  ContactsArraySchemaContext;

export const createIndicationValidationSchema = validationSchema.shape({
  sellSideContacts: contactsArraySchema,
});

type ValidateIndicationSectionContext = CreateIndicationValidationSchemaContext & {
  isInvestorContactRequired: boolean;
  displayMissingContacts?: boolean;
};

export async function validateIndicationSection({
  values,
  isLimitIndicationsOnlyEnabled,
  isInvestorContactRequired,
  offeringType,
  displayMissingContacts,
  isIndicationPriceAsPercentageParEnabled,
  isOnlySharesUnitOnIndicationsEnabled,
}: {
  values: IndicationFormValues;
  isLimitIndicationsOnlyEnabled: boolean;
  isInvestorContactRequired: boolean;
  offeringType: OfferingType;
  displayMissingContacts: boolean;
  isIndicationPriceAsPercentageParEnabled: boolean;
  isOnlySharesUnitOnIndicationsEnabled: boolean;
}) {
  try {
    await createIndicationValidationSchema.validate(values, {
      abortEarly: false,
      context: {
        isLimitIndicationsOnlyEnabled,
        isInvestorContactRequired,
        offeringType,
        displayMissingContacts,
        interestLevels: values.interestLevels,
        createMode: false,
        isIndicationPriceAsPercentageParEnabled,
        isOnlySharesUnitOnIndicationsEnabled,
      } satisfies ValidateIndicationSectionContext,
    });

    return {};
  } catch (e) {
    return yupToFormErrors(e);
  }
}
